import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Upload,
  Edit2,
  Trash2,
  Users,
  Mail,
  Phone,
  Send,
  ListPlus,
  ChevronRight,
  Download,
  MessageSquare,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { audienceContactsAPI, audienceListsAPI, audienceCampaignsAPI, referenceAPI, storiesAPI } from '../lib/api';
import { formatDate, formatNumber, getStatusColor, cn } from '../lib/utils';
import { toast } from 'sonner';

function StatusBadge({ status }) {
  const colorMap = {
    success: 'bg-green-900/20 text-green-400 border-green-900/50',
    warning: 'bg-amber-900/20 text-amber-400 border-amber-900/50',
    error: 'bg-red-900/20 text-red-400 border-red-900/50',
    neutral: 'bg-slate-800 text-slate-400 border-slate-700',
    info: 'bg-blue-900/20 text-blue-400 border-blue-900/50',
  };
  const statusType = getStatusColor(status);
  return (
    <Badge className={`${colorMap[statusType]} border capitalize`}>
      {status}
    </Badge>
  );
}

export function Audiences() {
  const [activeTab, setActiveTab] = useState('contacts');
  
  // Contacts State
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactSearch, setContactSearch] = useState('');
  const [contactPage, setContactPage] = useState(1);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  // Lists State
  const [lists, setLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [showListModal, setShowListModal] = useState(false);
  
  // Campaigns State
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignStep, setCampaignStep] = useState(1);
  
  // Reference Data
  const [organisations, setOrganisations] = useState([]);
  const [clients, setClients] = useState([]);
  const [stories, setStories] = useState([]);
  
  // Contact Form
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    title: '',
    organisation_name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    source: '',
    notes: '',
    is_active: true,
  });

  // List Form
  const [listForm, setListForm] = useState({
    name: '',
    description: '',
    channel: 'email',
    client_id: '',
  });

  // Campaign Form
  const [campaignForm, setCampaignForm] = useState({
    campaign_name: '',
    channel: 'email',
    story_id: '',
    client_id: '',
    list_id: '',
    subject: '',
    message_body: '',
    from_name: 'QuantumCast',
    from_email: '',
    from_number: '',
  });

  useEffect(() => {
    loadReferenceData();
    loadContacts();
    loadLists();
    loadCampaigns();
  }, []);

  useEffect(() => {
    loadContacts();
  }, [contactSearch, contactPage]);

  const loadReferenceData = async () => {
    try {
      const [orgsRes, clientsRes, storiesRes] = await Promise.all([
        referenceAPI.getOrganisations(),
        referenceAPI.getClients(),
        storiesAPI.getAll({ status: 'published', page_size: 100 }),
      ]);
      setOrganisations(orgsRes.data);
      setClients(clientsRes.data);
      setStories(storiesRes.data.stories);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const loadContacts = async () => {
    try {
      setContactsLoading(true);
      const response = await audienceContactsAPI.getAll({
        search: contactSearch,
        page: contactPage,
        page_size: 20,
      });
      setContacts(response.data.contacts);
      setContactsTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setContactsLoading(false);
    }
  };

  const loadLists = async () => {
    try {
      setListsLoading(true);
      const response = await audienceListsAPI.getAll();
      setLists(response.data);
    } catch (error) {
      console.error('Failed to load lists:', error);
    } finally {
      setListsLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      const response = await audienceCampaignsAPI.getAll();
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const handleSaveContact = async () => {
    try {
      if (editingContact) {
        await audienceContactsAPI.update(editingContact.id, contactForm);
        toast.success('Contact updated');
      } else {
        await audienceContactsAPI.create(contactForm);
        toast.success('Contact created');
      }
      setShowContactModal(false);
      setEditingContact(null);
      resetContactForm();
      loadContacts();
    } catch (error) {
      toast.error('Failed to save contact');
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await audienceContactsAPI.delete(id);
      toast.success('Contact deleted');
      loadContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleSaveList = async () => {
    try {
      await audienceListsAPI.create(listForm);
      toast.success('List created');
      setShowListModal(false);
      resetListForm();
      loadLists();
    } catch (error) {
      toast.error('Failed to create list');
    }
  };

  const handleSaveCampaign = async () => {
    try {
      await audienceCampaignsAPI.create(campaignForm);
      toast.success('Campaign created');
      setShowCampaignModal(false);
      setCampaignStep(1);
      resetCampaignForm();
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const handleSendCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to send this campaign?')) return;
    try {
      await audienceCampaignsAPI.send(id);
      toast.success('Campaign sent');
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to send campaign');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await audienceContactsAPI.bulkImport(formData);
      toast.success(`Import complete: ${response.data.created} contacts added, ${response.data.skipped} skipped`);
      loadContacts();
    } catch (error) {
      toast.error('Failed to import contacts');
    }

    e.target.value = '';
  };

  const resetContactForm = () => {
    setContactForm({
      first_name: '',
      last_name: '',
      title: '',
      organisation_name: '',
      email: '',
      phone: '',
      linkedin_url: '',
      source: '',
      notes: '',
      is_active: true,
    });
  };

  const resetListForm = () => {
    setListForm({
      name: '',
      description: '',
      channel: 'email',
      client_id: '',
    });
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      campaign_name: '',
      channel: 'email',
      story_id: '',
      client_id: '',
      list_id: '',
      subject: '',
      message_body: '',
      from_name: 'QuantumCast',
      from_email: '',
      from_number: '',
    });
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      title: contact.title || '',
      organisation_name: contact.organisation_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      linkedin_url: contact.linkedin_url || '',
      source: contact.source || '',
      notes: contact.notes || '',
      is_active: contact.is_active !== false,
    });
    setShowContactModal(true);
  };

  return (
    <div className="space-y-6" data-testid="audiences-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-text-primary uppercase tracking-tight">
          Proprietary Audiences
        </h1>
        <p className="text-text-secondary mt-1">Manage contacts and send campaigns</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-bg-tertiary">
          <TabsTrigger value="contacts">Contacts & Lists</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
        </TabsList>

        {/* Contacts & Lists Tab */}
        <TabsContent value="contacts" className="space-y-6 mt-6">
          {/* Contacts Section */}
          <Card className="bg-surface-card border-border-default">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-heading text-text-primary">
                <Users className="w-5 h-5 inline mr-2" />
                Contacts ({formatNumber(contactsTotal)})
              </CardTitle>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <Input
                    type="file"
                    accept=".csv,.xlsx"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <Button variant="outline" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV
                    </span>
                  </Button>
                </label>
                <Button onClick={() => setShowContactModal(true)} data-testid="add-contact-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                  <Input
                    placeholder="Search contacts..."
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    className="pl-10 bg-bg-tertiary border-border-default"
                    data-testid="contact-search-input"
                  />
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contactsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : contacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-text-tertiary">
                        No contacts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.map((contact) => (
                      <TableRow key={contact.id} className="table-row">
                        <TableCell className="table-cell font-medium">{contact.full_name || '-'}</TableCell>
                        <TableCell className="table-cell">{contact.title || '-'}</TableCell>
                        <TableCell className="table-cell">{contact.organisation_name || '-'}</TableCell>
                        <TableCell className="table-cell">{contact.email || '-'}</TableCell>
                        <TableCell className="table-cell">{contact.phone || '-'}</TableCell>
                        <TableCell className="table-cell">
                          <Badge className={contact.is_active ? 'badge-success' : 'badge-neutral'}>
                            {contact.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditContact(contact)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteContact(contact.id)}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Lists Section */}
          <Card className="bg-surface-card border-border-default">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-heading text-text-primary">
                <ListPlus className="w-5 h-5 inline mr-2" />
                Lists ({lists.length})
              </CardTitle>
              <Button onClick={() => setShowListModal(true)} data-testid="add-list-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create List
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>List Name</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Contacts</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : lists.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-text-tertiary">
                        No lists created yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    lists.map((list) => (
                      <TableRow key={list.id} className="table-row">
                        <TableCell className="table-cell font-medium">{list.name}</TableCell>
                        <TableCell className="table-cell">
                          <Badge variant="outline" className="capitalize">{list.channel}</Badge>
                        </TableCell>
                        <TableCell className="table-cell">{formatNumber(list.contact_count)}</TableCell>
                        <TableCell className="table-cell">{formatDate(list.created_at)}</TableCell>
                        <TableCell className="table-cell">
                          <Button variant="ghost" size="sm">
                            View
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Button onClick={() => setShowCampaignModal(true)} data-testid="new-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>

          <Card className="bg-surface-card border-border-default">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-transparent">
                    <TableHead>Campaign Name</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Delivered</TableHead>
                    <TableHead>Opened</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <div className="animate-spin w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : campaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-text-tertiary">
                        No campaigns created yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    campaigns.map((campaign) => (
                      <TableRow key={campaign.id} className="table-row">
                        <TableCell className="table-cell font-medium">{campaign.campaign_name}</TableCell>
                        <TableCell className="table-cell">
                          <Badge variant="outline" className="capitalize">
                            {campaign.channel === 'sms' ? (
                              <><MessageSquare className="w-3 h-3 mr-1" /> SMS</>
                            ) : (
                              <><Mail className="w-3 h-3 mr-1" /> Email</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">
                          <StatusBadge status={campaign.status} />
                        </TableCell>
                        <TableCell className="table-cell">{formatNumber(campaign.total_recipients)}</TableCell>
                        <TableCell className="table-cell">{formatNumber(campaign.total_delivered)}</TableCell>
                        <TableCell className="table-cell">
                          {campaign.channel === 'email' ? formatNumber(campaign.total_opened) : '-'}
                        </TableCell>
                        <TableCell className="table-cell">{formatDate(campaign.sent_at)}</TableCell>
                        <TableCell className="table-cell">
                          {campaign.status === 'draft' && (
                            <Button 
                              size="sm" 
                              onClick={() => handleSendCampaign(campaign.id)}
                              data-testid={`send-campaign-${campaign.id}`}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Send
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-lg bg-surface-card border-border-default">
          <DialogHeader>
            <DialogTitle className="text-text-primary">
              {editingContact ? 'Edit Contact' : 'Add Contact'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  value={contactForm.first_name}
                  onChange={(e) => setContactForm({...contactForm, first_name: e.target.value})}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={contactForm.last_name}
                  onChange={(e) => setContactForm({...contactForm, last_name: e.target.value})}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>
            <div>
              <Label>Title / Position</Label>
              <Input
                value={contactForm.title}
                onChange={(e) => setContactForm({...contactForm, title: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>
            <div>
              <Label>Organisation</Label>
              <Input
                value={contactForm.organisation_name}
                onChange={(e) => setContactForm({...contactForm, organisation_name: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                  placeholder="+44..."
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>
            <div>
              <Label>LinkedIn URL</Label>
              <Input
                value={contactForm.linkedin_url}
                onChange={(e) => setContactForm({...contactForm, linkedin_url: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>
            <div>
              <Label>Source</Label>
              <Input
                value={contactForm.source}
                onChange={(e) => setContactForm({...contactForm, source: e.target.value})}
                placeholder="e.g., Conference, LinkedIn, Referral"
                className="bg-bg-tertiary border-border-default"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={contactForm.notes}
                onChange={(e) => setContactForm({...contactForm, notes: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={contactForm.is_active}
                onCheckedChange={(checked) => setContactForm({...contactForm, is_active: checked})}
              />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>Cancel</Button>
            <Button onClick={handleSaveContact} data-testid="save-contact-btn">
              {editingContact ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* List Modal */}
      <Dialog open={showListModal} onOpenChange={setShowListModal}>
        <DialogContent className="sm:max-w-lg bg-surface-card border-border-default">
          <DialogHeader>
            <DialogTitle className="text-text-primary">Create List</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>List Name</Label>
              <Input
                value={listForm.name}
                onChange={(e) => setListForm({...listForm, name: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={listForm.description}
                onChange={(e) => setListForm({...listForm, description: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>
            <div>
              <Label>Channel</Label>
              <Select value={listForm.channel} onValueChange={(v) => setListForm({...listForm, channel: v})}>
                <SelectTrigger className="bg-bg-tertiary border-border-default">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Linked Client (Optional)</Label>
              <Select value={listForm.client_id} onValueChange={(v) => setListForm({...listForm, client_id: v})}>
                <SelectTrigger className="bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowListModal(false)}>Cancel</Button>
            <Button onClick={handleSaveList} data-testid="save-list-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="sm:max-w-xl bg-surface-card border-border-default">
          <DialogHeader>
            <DialogTitle className="text-text-primary">New Campaign</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignForm.campaign_name}
                onChange={(e) => setCampaignForm({...campaignForm, campaign_name: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Channel</Label>
                <Select value={campaignForm.channel} onValueChange={(v) => setCampaignForm({...campaignForm, channel: v})}>
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Target List</Label>
                <Select value={campaignForm.list_id} onValueChange={(v) => setCampaignForm({...campaignForm, list_id: v})}>
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue placeholder="Select list" />
                  </SelectTrigger>
                  <SelectContent>
                    {lists.filter(l => l.channel === campaignForm.channel || l.channel === 'both').map((list) => (
                      <SelectItem key={list.id} value={list.id}>{list.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Linked Story (Optional)</Label>
              <Select value={campaignForm.story_id} onValueChange={(v) => setCampaignForm({...campaignForm, story_id: v})}>
                <SelectTrigger className="bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="Select story" />
                </SelectTrigger>
                <SelectContent>
                  {stories.map((story) => (
                    <SelectItem key={story.id} value={story.id}>
                      {story.headline?.substring(0, 50)}...
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {campaignForm.channel === 'email' && (
              <>
                <div>
                  <Label>Subject Line</Label>
                  <Input
                    value={campaignForm.subject}
                    onChange={(e) => setCampaignForm({...campaignForm, subject: e.target.value})}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                <div>
                  <Label>From Name</Label>
                  <Input
                    value={campaignForm.from_name}
                    onChange={(e) => setCampaignForm({...campaignForm, from_name: e.target.value})}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
              </>
            )}

            <div>
              <Label>Message Body</Label>
              <Textarea
                value={campaignForm.message_body}
                onChange={(e) => setCampaignForm({...campaignForm, message_body: e.target.value})}
                placeholder={campaignForm.channel === 'sms' ? 'SMS message (use {first_name} for personalization)' : 'Email body (HTML supported)'}
                className="bg-bg-tertiary border-border-default min-h-[120px]"
              />
              {campaignForm.channel === 'sms' && (
                <p className="text-xs text-text-tertiary mt-1">
                  {campaignForm.message_body.length}/160 characters
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveCampaign} 
              disabled={!campaignForm.campaign_name || !campaignForm.message_body}
              data-testid="save-campaign-btn"
            >
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Audiences;
